/**
 * @fileoverview Um card do quadro compartilhado, aberto: só o que o link mostra
 * (a API manda campo a campo). Tudo como texto: a descrição chega sem HTML e
 * nada aqui vira link, imagem ou ação.
 */

import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { X, CheckSquare, Square } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { formatDateTime } from "../../utils/formatLocaleDate";
import { formatCurrencyValue } from "../../components/KanbanBoard/KanbanCard";
import { denialOf, fetchSharedCard } from "./sharedBoardApi";

const t = (key, options) => i18n.t(`sharedBoard.card.${key}`, options);

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const textPrimary = dark ? "rgba(255,255,255,0.92)" : "#0f172a";
  const textSecondary = dark ? "rgba(255,255,255,0.68)" : "#475569";
  const textMuted = dark ? "rgba(255,255,255,0.45)" : "#64748b";
  const line = dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
  return {
    paper: {
      borderRadius: 18,
      background: dark ? "#1c1e23" : "#ffffff",
      width: "100%",
      maxWidth: 520,
      margin: 16,
      maxHeight: "calc(100% - 32px)",
      [theme.breakpoints.down("xs")]: { margin: 8, maxHeight: "calc(100% - 16px)" },
    },
    header: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 20px 12px", borderBottom: `1px solid ${line}` },
    headText: { flex: 1, minWidth: 0 },
    title: { fontSize: 17, fontWeight: 600, color: textPrimary, lineHeight: 1.35, margin: 0, overflowWrap: "anywhere" },
    column: { fontSize: 13, color: textSecondary, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 6 },
    dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
    close: {
      background: "transparent",
      border: "none",
      padding: 6,
      margin: "-6px -6px 0 0",
      cursor: "pointer",
      color: textMuted,
      display: "inline-flex",
      borderRadius: 8,
      "&:hover": { color: textPrimary, background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.045)" },
    },
    body: { padding: "16px 20px 22px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 },
    grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px 18px", [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" } },
    item: { display: "flex", flexDirection: "column", gap: 3, minWidth: 0 },
    label: { fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: textMuted },
    value: { fontSize: 14, color: textPrimary, lineHeight: 1.45, overflowWrap: "anywhere" },
    section: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 14, borderTop: `1px solid ${line}` },
    checklist: { display: "flex", flexDirection: "column", gap: 6, margin: 0, padding: 0, listStyle: "none" },
    checkItem: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 14, color: textPrimary, "& svg": { flexShrink: 0, marginTop: 2, color: textMuted } },
    done: { color: textMuted, textDecoration: "line-through" },
    description: { fontSize: 14, color: textPrimary, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: 0 },
    center: { display: "flex", justifyContent: "center", padding: 28, color: textMuted },
    error: { fontSize: 14, color: textSecondary, textAlign: "center", margin: 0, padding: "12px 0" },
  };
});

const Item = ({ label, children }) => {
  const classes = useStyles();
  if (children === null || children === undefined || children === "") return null;
  return (
    <div className={classes.item}>
      <span className={classes.label}>{label}</span>
      <span className={classes.value}>{children}</span>
    </div>
  );
};

/**
 * `lead`: o card da lista (o nome aparece já); `onDenied`: o link deixou de
 * abrir (revogado, vencido, senha nova) e a página relê a situação.
 */
const SharedCardDialog = ({ token, lead, column, onClose, onDenied }) => {
  const classes = useStyles();
  const [card, setCard] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setCard(null);
    setFailed(false);
    fetchSharedCard(token, lead.id)
      .then(({ data }) => {
        if (alive) setCard(data);
      })
      .catch((err) => {
        if (!alive) return;
        setFailed(true);
        const denial = denialOf(err);
        if (denial !== "error" && denial !== "rateLimited" && err?.response?.data?.error !== "ERR_SHARE_CARD_NOT_FOUND") onDenied();
      });
    return () => {
      alive = false;
    };
  }, [token, lead.id, onDenied]);

  const priorities = i18n.t("sharedBoard.card.priorities", { returnObjects: true });

  return (
    <Dialog open onClose={onClose} classes={{ paper: classes.paper }} maxWidth={false} aria-labelledby="shared-card-title">
      <div className={classes.header}>
        <div className={classes.headText}>
          <h2 id="shared-card-title" className={classes.title}>
            {lead.name}
          </h2>
          {column && (
            <p className={classes.column}>
              <span className={classes.dot} style={{ background: column.color }} />
              {column.name}
            </p>
          )}
        </div>
        <button type="button" className={classes.close} onClick={onClose} aria-label={t("close")}>
          <X size={18} />
        </button>
      </div>
      <div className={classes.body}>
        {!card && !failed && (
          <div className={classes.center}>
            <CircularProgress size={22} color="inherit" />
          </div>
        )}
        {failed && <p className={classes.error}>{t("loadError")}</p>}
        {card && (
          <>
            <div className={classes.grid}>
              <Item label={t("priority")}>{priorities[card.priority] || card.priority}</Item>
              <Item label={t("dueAt")}>{card.dueAt ? formatDateTime(card.dueAt) : null}</Item>
              <Item label={t("assignee")}>{card.assignee || t("nobody")}</Item>
              <Item label={t("createdAt")}>{formatDateTime(card.createdAt)}</Item>
              <Item label={t("products")}>{card.products.length ? card.products.join(", ") : null}</Item>
              <Item label={t("checklist")}>
                {card.checklist ? t("checklistProgress", { done: card.checklist.done, total: card.checklist.total }) : null}
              </Item>
              <Item label={t("phone")}>{card.phone}</Item>
              <Item label={t("email")}>{card.email}</Item>
              <Item label={t("cpf")}>{card.cpf}</Item>
              <Item label={t("address")}>{card.address}</Item>
              <Item label={t("serviceOrder")}>{card.serviceOrder}</Item>
              {card.fields.map((field) => (
                <Item key={field.id} label={field.label || t("field")}>
                  {field.type === "currency" ? formatCurrencyValue(field.value) : field.value}
                </Item>
              ))}
            </div>
            {card.checklistItems.length > 0 && (
              <div className={classes.section}>
                <span className={classes.label}>{t("checklist")}</span>
                <ul className={classes.checklist}>
                  {card.checklistItems.map((item, index) => (
                    <li key={index} className={classes.checkItem}>
                      {item.done ? <CheckSquare size={15} /> : <Square size={15} />}
                      <span className={item.done ? classes.done : undefined}>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {card.description && (
              <div className={classes.section}>
                <span className={classes.label}>{t("description")}</span>
                <p className={classes.description}>{card.description}</p>
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default SharedCardDialog;
